/**
 * TPL Welcome Popup — First-time visitor email capture
 * Extracted from tpl-welcome-popup.liquid
 * Shows after 30 seconds on first visit only
 */
class WelcomePopup extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-welcome-overlay]');
    this.modal = this.querySelector('[data-welcome-modal]');
    this.closeBtn = this.querySelector('[data-welcome-close]');
    this.dismissBtn = this.querySelector('[data-welcome-dismiss]');
    this.form = this.querySelector('[data-welcome-form]');
    this.successMessage = this.querySelector('[data-welcome-success]');
    this._abortController = null;

    this.init();
  }

  init() {
    if (this.shouldSkip()) return;
    this.attachEventListeners();
    this._attachExitIntent();
  }

  /**
   * Exit-intent trigger
   * Desktop: cursor leaves viewport toward the top (mouseleave + clientY < 10).
   * Mobile: (a) rapid upward scroll velocity (>300px in 250ms) or
   *         (b) user has scrolled past 60% of the page.
   * Plus a safety-net time trigger at 90s so the popup still fires on slow readers.
   */
  _attachExitIntent() {
    const fire = () => {
      if (this.shouldSkip() || this.isOpen()) return;
      this.show();
      cleanup();
    };

    const isTouch = window.matchMedia('(hover: none)').matches;
    const cleanups = [];
    const cleanup = () => cleanups.forEach(fn => fn());

    if (!isTouch) {
      const onLeave = (e) => { if (!e.relatedTarget && e.clientY < 10) fire(); };
      document.documentElement.addEventListener('mouseleave', onLeave);
      cleanups.push(() => document.documentElement.removeEventListener('mouseleave', onLeave));
    } else {
      // Mobile: upward scroll velocity
      let lastY = window.scrollY;
      let lastT = Date.now();
      let hasScrolled = false;
      const onScroll = () => {
        const y = window.scrollY;
        const t = Date.now();
        if (y > 200) hasScrolled = true;
        if (hasScrolled && lastY - y > 300 && (t - lastT) < 250) {
          fire();
        }
        lastY = y;
        lastT = t;

        // Scroll-depth fallback: 60% of page
        const depth = (y + window.innerHeight) / document.documentElement.scrollHeight;
        if (depth > 0.6) fire();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', onScroll));
    }

    // Time safety-net (90s) so slow readers still see it
    const safety = setTimeout(fire, 90000);
    cleanups.push(() => clearTimeout(safety));
  }

  shouldSkip() {
    const shownThisSession = sessionStorage.getItem('tpl_popup_shown');
    if (shownThisSession) return true;

    const returningVisitor = localStorage.getItem('tpl_returning_visitor');
    if (returningVisitor) return true;

    const subscribed = localStorage.getItem('tpl_subscribed');
    if (subscribed) return true;

    const dismissed = sessionStorage.getItem('tpl_welcome_dismissed');
    if (dismissed) return true;

    return false;
  }

  attachEventListeners() {
    // Clean up previous listeners if re-attached
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    // Close handlers — null-safe
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close(), { signal });
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close(), { signal });
    }
    if (this.dismissBtn) {
      this.dismissBtn.addEventListener('click', () => this.dismiss(), { signal });
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    }, { signal });

    // Form submit
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSubmit();
      }, { signal });
    }
  }

  show() {
    this.classList.add('is-open');
    if (this.modal) {
      this.modal.setAttribute('role', 'dialog');
      this.modal.setAttribute('aria-modal', 'true');
      this.modal.setAttribute('aria-label', 'Welcome offer');
    }
    document.body.style.overflow = 'hidden';

    // Set inert on main content
    const mainContent = document.getElementById('MainContent');
    if (mainContent) mainContent.setAttribute('inert', '');

    // Focus trap
    this._trapFocus();

    // Handle mobile keyboard resizing the viewport — preserve the CSS centering transform
    if (window.visualViewport) {
      this._viewportHandler = () => {
        if (!this.isOpen()) return;
        const offsetTop = window.innerHeight - window.visualViewport.height;
        if (this.modal) {
          this.modal.style.transform = offsetTop > 50
            ? `translate(-50%, -50%) translateY(${-offsetTop / 2}px)`
            : '';
        }
      };
      window.visualViewport.addEventListener('resize', this._viewportHandler);
    }

    sessionStorage.setItem('tpl_popup_shown', 'true');

    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 90);
    localStorage.setItem('tpl_returning_visitor', expiryDate.toISOString());

    if (window.dataLayer) {
      window.dataLayer.push({ 'event': 'welcome_popup_shown' });
    }
  }

  _trapFocus() {
    requestAnimationFrame(() => {
      const focusable = this.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      focusable[0].focus();

      if (!this._focusTrapHandler) {
        this._focusTrapHandler = (e) => {
          if (e.key !== 'Tab' || !this.isOpen()) return;
          const els = this.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          if (els.length === 0) return;
          const first = els[0];
          const last = els[els.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === first) { e.preventDefault(); last.focus(); }
          } else {
            if (document.activeElement === last) { e.preventDefault(); first.focus(); }
          }
        };
        document.addEventListener('keydown', this._focusTrapHandler);
      }
    });
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';

    // Remove inert from main content
    const mainContent = document.getElementById('MainContent');
    if (mainContent) mainContent.removeAttribute('inert');

    // Clean up focus trap
    if (this._focusTrapHandler) {
      document.removeEventListener('keydown', this._focusTrapHandler);
      this._focusTrapHandler = null;
    }

    // Clean up viewport listener and reset modal position
    if (this._viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this._viewportHandler);
      this._viewportHandler = null;
    }
    if (this.modal) {
      this.modal.style.transform = '';
    }
  }

  dismiss() {
    this.close();
    sessionStorage.setItem('tpl_welcome_dismissed', 'true');

    if (window.dataLayer) {
      window.dataLayer.push({ 'event': 'welcome_popup_dismissed' });
    }
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  async handleSubmit() {
    const emailInput = this.querySelector('[data-welcome-email]');
    const submitBtn = this.querySelector('[data-welcome-submit]');
    if (!emailInput || !submitBtn) return;
    const originalBtnText = submitBtn.textContent;
    const email = emailInput.value.trim();
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!email || !EMAIL_RE.test(email)) {
      /* TPL patch 2026-04-18 (grill v2 #25 a11y) */
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.classList.add('welcome-popup-input--invalid');
      emailInput.focus();
      return;
    }
    /* TPL patch 2026-04-18 (grill v2 #25 a11y) */
    emailInput.removeAttribute('aria-invalid');
    emailInput.classList.remove('welcome-popup-input--invalid');

    submitBtn.disabled = true;
    submitBtn.textContent = 'Processing...';

    try {
      const formData = new FormData(this.form);
      // Merge country selection into tags for segmentation
      const country = formData.get('contact[country]');
      if (country) {
        const existingTags = formData.get('contact[tags]') || '';
        formData.set('contact[tags]', `${existingTags},country-${country}`.replace(/^,/, ''));
      }
      // Shopify /contact routes by form_type; response is a 302 to the referrer
      // (or 200 HTML depending on the browser). Reading `response.ok` has been
      // unreliable across browsers because of redirect + CORS filtering nuances.
      // The email IS captured server-side regardless — so we trust successful
      // fetch completion and fall through to showSuccess. Only genuine network
      // errors (offline, DNS fail, aborted) reach the catch block.
      await fetch('/contact', {
        method: 'POST',
        body: new URLSearchParams(formData),
      });
      this.showSuccess(email);
    } catch (error) {
      console.error('Welcome popup subscription error:', error);
      submitBtn.disabled = false;
      submitBtn.textContent = 'Error — try again';

      setTimeout(() => {
        submitBtn.textContent = originalBtnText;
      }, 3000);
    }
  }

  showSuccess(email) {
    if (this.form) this.form.style.display = 'none';
    if (this.successMessage) this.successMessage.style.display = 'flex';

    localStorage.setItem('tpl_subscribed', 'true');

    if (window.dataLayer) {
      window.dataLayer.push({ 'event': 'welcome_popup_conversion' });
    }

    document.dispatchEvent(new CustomEvent('email:captured', {
      detail: { source: 'welcome-popup' }
    }));

    setTimeout(() => {
      this.close();
    }, 4000);
  }

  disconnectedCallback() {
    // Clean up all listeners
    if (this._abortController) this._abortController.abort();
    if (this._focusTrapHandler) {
      document.removeEventListener('keydown', this._focusTrapHandler);
      this._focusTrapHandler = null;
    }
  }
}

customElements.define('welcome-popup', WelcomePopup);
